import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Check, X, GitFork, Loader2, MessageCircle, Cake, UserPlus, LifeBuoy, PhoneMissed, AtSign, Plus, ArrowLeft, Send, Search, Lock, FileText } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api, formatApiError, fileUrl } from "../lib/api";
import { BottomSheet } from "./BottomSheet";
import { Avatar } from "../pages/Hangouts";
import { useAuth } from "../context/AuthContext";

const ago = (d) => { try { return formatDistanceToNow(new Date(d), { addSuffix: true }); } catch { return ""; } };

export function MessagesSheet({ open, onClose, onChange, canModerate }) {
  const { user } = useAuth();
  const [view, setView] = useState("list"); // list | thread | compose
  const [activeUser, setActiveUser] = useState(null);
  const [threads, setThreads] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [support, setSupport] = useState({ messages: [], unread: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [roles, setRoles] = useState({});
  const [delivery, setDelivery] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, n, s] = await Promise.all([
        api.get("/messages/threads").catch(() => ({ data: [] })),
        api.get("/notifications").catch(() => ({ data: [] })),
        api.get("/support/inbox").catch(() => ({ data: { messages: [], unread: 0 } })),
      ]);
      setThreads(t.data);
      const seen = new Set();
      const uniqNotifs = (n.data || []).filter((x) => (seen.has(x.id) ? false : seen.add(x.id)));
      setNotifs(uniqNotifs);
      setSupport(s.data || { messages: [], unread: 0 });
      if (uniqNotifs.some((x) => ["comment", "mention", "missed_call"].includes(x.type) && !x.read)) {
        api.post("/notifications/read-all").then(() => window.dispatchEvent(new Event("fv-refresh-badge"))).catch(() => {});
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { setView("list"); load(); } }, [open, load]);

  const respond = async (id, accept, role) => {
    setBusy(id);
    try {
      await api.post(`/notifications/${id}/respond`, { accept, role });
      toast.success(accept ? "Approved" : "Dismissed");
      await load();
      onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const openThread = (otherId, name) => { setActiveUser({ id: otherId, name }); setView("thread"); };

  const deepLink = (n) => {
    if (!n.link_kind) return;
    onClose();
    if (n.link_kind === "hangout") navigate(`/hangouts?open=${n.link_id}`);
    else if (n.link_kind === "reunion") navigate(`/reunions?open=${n.link_id}`);
    else if (n.link_kind === "timecapsule") navigate(`/vault?tc=1`);
  };

  const title = view === "thread" ? (activeUser?.name || "Chat") : view === "compose" ? "New message" : "Inbox";
  const leftAction = view !== "list"
    ? <button onClick={() => { setView("list"); load(); }} data-testid="inbox-back-btn" className="grid place-items-center h-9 w-9 rounded-full" style={{ background: "var(--overlay)" }}><ArrowLeft size={18} /></button>
    : null;

  return (
    <BottomSheet open={open} onClose={onClose} title={title} height="94dvh" leftAction={leftAction} fill={view === "thread"}>
      {view === "thread" && activeUser ? (
        <ThreadView other={activeUser} me={user} onBadge={() => window.dispatchEvent(new Event("fv-refresh-badge"))} />
      ) : view === "compose" ? (
        <ComposeView me={user} onPick={(u) => openThread(u.id, u.name)} />
      ) : loading ? (
        <div className="grid place-items-center py-10"><Loader2 className="animate-spin gold-text" /></div>
      ) : (
        <>
          <div className="space-y-4">
            {notifs.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>System</div>
                <div className="space-y-2">
                  {notifs.map((n) => {
                    if (["vault_delivery", "vault_delivered", "vault_opened"].includes(n.type)) {
                      const isRecipient = n.type === "vault_delivery";
                      const open = () => {
                        setDelivery(n);
                        if (isRecipient && n.delivery_id) api.post(`/secret-vault/deliveries/${n.delivery_id}/open`).catch(() => {});
                      };
                      return (
                        <div key={n.id} data-testid={`notification-${n.id}`} onClick={open}
                          className="card-surface rounded-2xl p-3" style={{ opacity: n.read ? 0.7 : 1, cursor: "pointer", border: n.read ? undefined : "1px solid color-mix(in srgb, var(--accent) 45%, transparent)" }}>
                          <div className="flex items-center gap-2 mb-1.5 gold-text">
                            <Lock size={14} /><span className="text-[10px] uppercase tracking-widest">{isRecipient ? "From the Secret Vault" : "Your Secret Vault"}</span>
                          </div>
                          <p className="text-sm" style={{ color: "var(--text-primary)" }}>
                            {isRecipient ? <><b>{n.from_user_name}</b> left you “{n.delivery_title}”.</>
                              : n.type === "vault_opened" ? <><b>{n.to_name}</b> just opened “{n.delivery_title}”.</>
                              : <>“{n.delivery_title}” was just delivered to <b>{n.to_name}</b>.</>}
                          </p>
                          <span className="text-[11px] gold-text mt-1.5 inline-block" data-testid={`sv-open-${n.id}`}>{isRecipient ? "Tap to open →" : "Tap to see what you sent →"}</span>
                        </div>
                      );
                    }
                    if (["support", "missed_call", "comment", "mention"].includes(n.type)) {
                      const meta = {
                        support: ["Vault Support", <LifeBuoy size={14} key="i" />],
                        missed_call: ["Missed Call", <PhoneMissed size={14} key="i" />],
                        comment: ["New Comment", <MessageCircle size={14} key="i" />],
                        mention: ["You were mentioned", <AtSign size={14} key="i" />],
                      }[n.type];
                      const linkable = (n.type === "comment" || n.type === "mention") && !!n.link_kind;
                      return (
                        <div key={n.id} data-testid={`notification-${n.id}`} onClick={() => linkable && deepLink(n)}
                          className="card-surface rounded-2xl p-3" style={{ opacity: n.read ? 0.6 : 1, cursor: linkable ? "pointer" : "default" }}>
                          <div className="flex items-center gap-2 mb-1.5 gold-text">
                            {meta[1]}<span className="text-[10px] uppercase tracking-widest">{meta[0]}</span>
                          </div>
                          <p className="text-sm" style={{ color: "var(--text-primary)" }}>
                            {n.type === "support" ? n.text
                              : n.type === "missed_call" ? <span><b>{n.from_user_name}</b> tried to video call you.</span>
                              : n.type === "comment" ? <span><b>{n.from_user_name}</b> commented: "{n.text}"</span>
                              : <span><b>{n.from_user_name}</b> mentioned you: "{n.text}"</span>}
                          </p>
                          {linkable && <span className="text-[11px] gold-text mt-1.5 inline-block" data-testid={`notif-link-${n.id}`}>Tap to view →</span>}
                        </div>
                      );
                    }
                    return (
                      <div key={n.id} data-testid={`notification-${n.id}`} className="card-surface rounded-2xl p-3">
                        <div className="flex items-center gap-2 mb-1.5 gold-text">
                          {n.type === "birthday_change" ? <Cake size={14} /> : n.type === "join_request" ? <UserPlus size={14} /> : <GitFork size={14} />}
                          <span className="text-[10px] uppercase tracking-widest">
                            {n.type === "birthday_change" ? "Birthday Change" : n.type === "join_request" ? "Join Request" : n.type === "relation_removal" ? "Removal Request" : "Lineage Update"}
                          </span>
                        </div>
                        {n.type === "birthday_change" ? (
                          <p className="text-sm"><b>{n.from_user_name}</b> wants to change birthday to <b>{n.requested_birthday}</b>.</p>
                        ) : n.type === "join_request" ? (
                          <p className="text-sm"><b>{n.from_user_name}</b> ({n.user_email}) wants to join your family.</p>
                        ) : n.type === "relation_removal" ? (
                          <p className="text-sm"><b>{n.from_user_name}</b> wants to remove <b>{n.relative_name}</b> as their <b>{(n.relation || "").replace("_", "/")}</b>. Approve removal and unlink on the tree?</p>
                        ) : (
                          <p className="text-sm"><b>{n.from_user_name}</b> added <b>{n.relative_name}</b> as their <b>{(n.relation || "").replace("_", "/")}</b>. Add to tree?</p>
                        )}
                        {n.type === "join_request" && (
                          <div className="mt-2">
                            <label className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Assign role</label>
                            <select value={roles[n.id] || "user"} onChange={(e) => setRoles((r) => ({ ...r, [n.id]: e.target.value }))}
                              data-testid={`join-role-${n.id}`}
                              className="w-full mt-1 rounded-lg px-3 py-2 text-sm outline-none"
                              style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" }}>
                              <option value="user">Standard user</option>
                              <option value="organizer">Organizer</option>
                              <option value="admin">Admin</option>
                            </select>
                          </div>
                        )}
                        <div className="flex gap-2 mt-2">
                          <button onClick={() => respond(n.id, true, roles[n.id] || "user")} disabled={busy === n.id} data-testid={`notif-accept-${n.id}`}
                            className="flex-1 rounded-lg py-2 text-xs font-semibold flex items-center justify-center gap-1" style={{ background: "var(--accent)", color: "#fff" }}>
                            {busy === n.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={14} />} {(n.type === "join_request" || n.type === "relation_removal") ? "Approve" : "Yes"}
                          </button>
                          <button onClick={() => respond(n.id, false)} disabled={busy === n.id} data-testid={`notif-decline-${n.id}`}
                            className="rounded-lg px-4 py-2 text-xs flex items-center gap-1" style={{ background: "var(--overlay)", color: "var(--text-secondary)" }}>
                            <X size={14} /> No
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: "var(--text-muted)" }}>Messages</div>
              {support.messages && support.messages.length > 0 && (
                <button onClick={() => { onClose(); navigate("/support"); }} data-testid="thread-support"
                  className="w-full flex items-center gap-3 rounded-xl px-2 py-2.5 text-left mb-1" style={{ background: "var(--overlay)", border: "1px solid var(--border)" }}>
                  <div className="h-10 w-10 rounded-full grid place-items-center shrink-0" style={{ background: "color-mix(in srgb, var(--accent) 16%, transparent)" }}>
                    <LifeBuoy size={18} className="gold-text" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-semibold truncate">Vault Support</span>
                      <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>{ago(support.messages[support.messages.length - 1]?.created_at)}</span>
                    </div>
                    <div className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{support.messages[support.messages.length - 1]?.text}</div>
                  </div>
                  {support.unread > 0 && <span className="h-5 min-w-5 px-1 rounded-full grid place-items-center text-[10px] font-bold" style={{ background: "var(--accent)", color: "#fff" }}>{support.unread}</span>}
                </button>
              )}
              {threads.length === 0 ? (
                (!support.messages || support.messages.length === 0) && <p className="text-sm py-4 text-center" style={{ color: "var(--text-muted)" }}>No messages yet. Tap + to start a chat.</p>
              ) : (
                <div className="space-y-1">
                  {threads.map((t) => (
                    <button key={t.thread_id} onClick={() => openThread(t.other_id, t.name)} data-testid={`thread-${t.other_id}`}
                      className="w-full flex items-center gap-3 rounded-xl px-2 py-2.5 text-left" style={{ background: "var(--overlay)" }}>
                      <Avatar name={t.name} />
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold truncate">{t.name}</span>
                          <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>{ago(t.last_at)}</span>
                        </div>
                        <div className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{t.last}</div>
                      </div>
                      {t.unread > 0 && <span className="h-5 min-w-5 px-1 rounded-full grid place-items-center text-[10px] font-bold" style={{ background: "var(--accent)", color: "#fff" }}>{t.unread}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button onClick={() => setView("compose")} data-testid="new-message-btn"
            className="fixed right-6 z-[95] h-14 w-14 rounded-full grid place-items-center shadow-xl"
            style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 1.5rem)", background: "var(--accent)", color: "#fff" }}>
            <Plus size={26} />
          </button>
        </>
      )}
      {delivery && <VaultDeliveryViewer n={delivery} onClose={() => setDelivery(null)} />}
    </BottomSheet>
  );
}

function VaultDeliveryViewer({ n, onClose }) {
  const url = n.storage_path ? fileUrl(n.storage_path) : null;
  return (
    <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center" style={{ background: "rgba(0,0,0,0.65)" }} onClick={onClose} data-testid="sv-delivery-viewer">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl p-5 max-h-[90vh] overflow-y-auto" style={{ background: "var(--bg-elevated)" }}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 gold-text"><Lock size={16} /><span className="text-[10px] uppercase tracking-widest">From the Secret Vault</span></div>
          <button onClick={onClose} data-testid="sv-delivery-close"><X size={20} style={{ color: "var(--text-secondary)" }} /></button>
        </div>
        <div className="font-serif-display text-2xl mb-1" style={{ color: "var(--text-primary)" }}>{n.delivery_title}</div>
        <div className="text-xs mb-3" style={{ color: "var(--text-muted)" }}>{n.sent ? `Delivered to ${n.to_name || "your recipient"}` : `Left for you by ${n.from_user_name}`}</div>
        {(n.ai_note || n.note) && <div className="rounded-xl p-3 mb-3 text-sm italic" style={{ background: "color-mix(in srgb, var(--accent) 10%, transparent)", color: "var(--text-primary)" }}>“{n.ai_note || n.note}”</div>}
        {url && (
          <div className="rounded-xl overflow-hidden mb-3" style={{ background: "#000" }}>
            {n.media_kind === "video" ? <video src={url} controls className="w-full" />
              : n.media_kind === "image" ? <img src={url} alt="" className="w-full" />
              : n.media_kind === "audio" ? <audio src={url} controls className="w-full p-3" />
              : <a href={url} target="_blank" rel="noreferrer" className="flex items-center gap-2 p-4 text-sm gold-text"><FileText size={18} /> Open document</a>}
          </div>
        )}
        {n.body_md && (
          <div className="vault-md rounded-xl p-3 mb-3 text-sm" style={{ background: "var(--overlay)", color: "var(--text-primary)" }} data-testid="sv-delivery-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{n.body_md}</ReactMarkdown>
          </div>
        )}
        <div className="rounded-xl p-3" style={{ background: "var(--overlay)" }}>
          <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: "var(--text-muted)" }}>Conditions that unlocked this</div>
          <div className="text-sm" style={{ color: "var(--text-primary)" }}>{n.conditions_summary || "—"}</div>
        </div>
      </div>
    </div>
  );
}

function ComposeView({ me, onPick }) {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/families/members").then((r) => setMembers(r.data.filter((m) => (m.user_id || m.id) !== me?.id))).catch(() => {}); }, [me]);
  const results = members.filter((m) => (m.name || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="compose-view">
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} autoFocus placeholder="Search a family member…" data-testid="compose-search"
          className="w-full rounded-xl pl-9 pr-4 py-3 text-sm outline-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
      </div>
      <div className="space-y-1">
        {results.map((m) => {
          const uid = m.user_id || m.id;
          return (
            <button key={uid} onClick={() => onPick({ id: uid, name: m.name })} data-testid={`compose-member-${uid}`}
              className="w-full flex items-center gap-3 rounded-xl px-2 py-2.5 text-left" style={{ background: "var(--overlay)" }}>
              <Avatar name={m.name} />
              <span className="text-sm font-semibold">{m.name}</span>
            </button>
          );
        })}
        {results.length === 0 && <p className="text-sm py-6 text-center" style={{ color: "var(--text-muted)" }}>No members found.</p>}
      </div>
    </div>
  );
}

function ThreadView({ other, me, onBadge }) {
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const listRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/messages/${other.id}`);
      setMsgs(data.messages);
      onBadge && onBadge();
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [other.id, onBadge]);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [msgs, loading]);

  const send = async () => {
    if (!text.trim()) return;
    const body = text.trim();
    setText("");
    try {
      const { data } = await api.post(`/messages/${other.id}`, { text: body });
      setMsgs((m) => [...m, data]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="thread-view">
      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto no-scrollbar space-y-2 pb-2">
        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="animate-spin gold-text" /></div>
        ) : msgs.length === 0 ? (
          <p className="text-center text-sm py-10" style={{ color: "var(--text-muted)" }}>Say hello 👋</p>
        ) : msgs.map((m) => {
          const mine = m.sender_id === me?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`thread-msg-${m.id}`}>
              <div className="max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap"
                style={mine ? { background: "var(--accent)", color: "#fff" } : { background: "var(--overlay)", color: "var(--text-primary)" }}>
                {m.text}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 pt-2 shrink-0">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Message…" data-testid="thread-input"
          className="flex-1 rounded-full px-4 py-3 text-sm outline-none" style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
        <button onClick={send} data-testid="thread-send-btn" className="h-11 w-11 rounded-full grid place-items-center shrink-0" style={{ background: "var(--accent)", color: "#fff" }}>
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}
